import { useState, useEffect, useMemo, useCallback } from "react";
import moment from "moment";
import AxiosInstance, { TokenService } from "../../../../../services/Api";

const STATUS_CODE_TO_TEXT = {
  0: "Draft",
  1: "Submitted",
  2: "Approved",
  3: "Rejected",
  4: "Resubmitted",
  5: "Completed",
};

// Status text -> numeric code, for the status filter payload.
const STATUS_TEXT_TO_CODE = {
  Draft: 0,
  Submitted: 1,
  Approved: 2,
  Rejected: 3,
  Resubmitted: 4,
  Completed: 5,
};

const pickHmId = (item) => {
  if (!item || typeof item !== "object") return null;
  const candidates = [item.hm_id, item.hmId, item.hm, item.hm_user_id];
  for (const c of candidates) {
    if (c != null && c !== "" && !Number.isNaN(Number(c))) return Number(c);
  }
  return null;
};

export default function useLessonPlanApprovalData(
  tabStatus = "",
  weekFilter = "all",
) {
  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";
  const academic_id = clientData?.academic_year_id || "";

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [columnFilters, setColumnFilters] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const [topicOptions, setTopicOptions] = useState([]);
  const [subTopicOptions, setSubTopicOptions] = useState([]);

  // ------------------------------------------------------------------
  // Turn the columnFilters array into a quick-lookup map:
  //   { staff_name: "...", book: "...", topic: "...", ... }
  // ------------------------------------------------------------------
  const filterMap = useMemo(() => {
    const map = {};
    columnFilters.forEach((f) => {
      if (f?.id !== undefined) map[f.id] = f.value;
    });
    return map;
  }, [columnFilters]);

  // Safe string read for a single column filter
  const getStr = (id) => {
    const v = filterMap[id];
    if (v === undefined || v === null) return "";
    return String(v).trim();
  };

  // Date range filter (column id = "scheduled_date") — expected as [start, end]
  const { fromDate, toDate } = useMemo(() => {
    const range = filterMap.scheduled_date;
    if (!Array.isArray(range)) return { fromDate: "", toDate: "" };
    return {
      fromDate: range[0] ? moment(range[0]).format("YYYY-MM-DD") : "",
      toDate: range[1] ? moment(range[1]).format("YYYY-MM-DD") : "",
    };
  }, [filterMap.scheduled_date]);

  // Week-range derived from the "This week" toggle.
  const isWeek = weekFilter === "this_week" ? 1 : 0;

  // ------------------------------------------------------------------
  // Resolve the final status value for the payload.
  // ------------------------------------------------------------------
  const resolvedStatus = useMemo(() => {
    const raw = tabStatus || getStr("status_text");
    if (!raw) return "";
    if (/^\d+$/.test(raw)) return raw;
    const capitalized =
      raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
    const code = STATUS_TEXT_TO_CODE[capitalized];
    return code != null ? String(code) : raw;
  }, [tabStatus, filterMap.status_text]); // eslint-disable-line react-hooks/exhaustive-deps

  // ------------------------------------------------------------------
  // Build the payload dynamically
  // ------------------------------------------------------------------
  const buildPayload = useCallback(
    () => ({
      school_id,
      staff_id: "",
      class_id: getStr("class_code"),
      subject: getStr("book"),
      status: resolvedStatus || [1, 2, 3, 4, 5],
      section: getStr("section"),
      week: 0,
      topic: getStr("topic"),
      sub_topic: getStr("sub_topic"),
      medium: getStr("medium"),
      status_filter: "",
      search: "",
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      startDate: fromDate || "",
      endDate: toDate || "",
      period: "",
      day: "",
      academic_id: academic_id,
    }),
    [
      school_id,
      pagination.pageIndex,
      pagination.pageSize,
      filterMap,
      resolvedStatus,
      fromDate,
      toDate,
      isWeek,
    ], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ------------------------------------------------------------------
  // Fetch
  // ------------------------------------------------------------------
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const payload = buildPayload();
      const response = await AxiosInstance.post(
        `/book/lessonPlan/list`,
        payload,
      );
      if (response?.data?.status) {
        const rawList = response.data.data || [];
        const transformed = rawList.map((item, index) => ({
          id: item.id,
          def_id: item.def_id,
          item_id: item.item_id,
          hm_id: item.id,
          s_no: pagination.pageIndex * pagination.pageSize + index + 1,
          periods: item.periods,
          dayid: item.dayid,
          staff_name: item.staff_name,
          // column mappings (new response -> table columns)
          book: item.subject_name || "",
          topic: item.topic || "",
          sub_topic: item.sub_topic || "",
          class_code: item.class_name || "",
          section: item.section_ids || "",
          medium: item.medium || "",
          scheduled_date: item.schedule_date || "",
          status: item.status, // numeric
          status_text: item.status_label || "", // "Submitted" | "Approved" | "Rejected" | "Completed"
          // pass the original transactions array through for the history panel
          history: (item.trans || []).map((t) => ({
            user_name: t.action_by_name || "-",
            topic: t.topic || "-", // ✅ from the trans entry
            sub_topic: t.sub_topic || "-",
            status: t.status,
            status_text: t.action || "",
            updated_on: t.created_at,
            comments: t.comments,
          })),
          // keep the raw item in case the view page needs it
          _raw: item,
        }));

        setData(transformed);
        setRowCount(response.data.pagination?.total || transformed.length);

        // populate dropdown options (unique, non-empty)
        const topics = [
          ...new Set(
            rawList
              .map((i) => i.topic)
              .filter((t) => t && String(t).trim() !== ""),
          ),
        ];
        const subs = [
          ...new Set(
            rawList
              .map((i) => i.sub_topic)
              .filter((t) => t && String(t).trim() !== ""),
          ),
        ];
        setTopicOptions(topics);
        setSubTopicOptions(subs);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (e) {
      console.error("Lesson plan list fetch error:", e);
      setData([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [buildPayload, pagination.pageIndex, pagination.pageSize]);

  // ------------------------------------------------------------------
  // Reset to page 0 whenever filters / tab / week toggle change
  // ------------------------------------------------------------------
  useEffect(() => {
    setPagination((prev) =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 },
    );
  }, [columnFilters, weekFilter, tabStatus]);

  // Fetch whenever the payload meaningfully changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleColumnFiltersChange = (updaterOrValue) => {
    setColumnFilters((prev) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(prev)
        : updaterOrValue,
    );
  };

  return {
    data,
    loading,
    rowCount,
    pagination,
    setPagination,
    columnFilters,
    handleColumnFiltersChange,
    topicOptions,
    subTopicOptions,
    refetch: fetchData,
  };
}
